<template>
	<section class="pc-specs">
		<slot name="specs-heading" />
		<div class="row row-padding" v-if="parts.cpus">
			<div class="col-md-5 pc-specs-column col-md-offset-1 pc-specs-first-col">
				<div class="specs-product">
					<p class="specs-category">Case</p>
					<div class="specs-info">
						<h5 class="specs-product-name">
							{{ parts.cases.name }} ({{ cart.case.colors }})
							<promo-sales-timed class="holiday-tag">
								<template>
									<holiday-tag :build="parts.cases" />
								</template>
							</promo-sales-timed>
						</h5>
						<template v-if="displayDetails">
							<p
								v-if="parts.cases.dimensions_in_inches"
								class="specs-description"
							>
								- Dimensions: {{ parts.cases.dimensions_in_inches }}
							</p>
							<p v-if="parts.cases.total_pc_weight" class="specs-description">
								- Weight: {{ parts.cases.total_pc_weight }}
							</p>
							<p v-if="+parts.cases.usb3_gen2_typeC" class="specs-description">
								- {{ parts.cases.usb3_gen2_typeC }}x USB 3.1 Gen 2 Type-C
							</p>
							<p v-if="+parts.cases.usb3_gen1_typeA" class="specs-description">
								- {{ parts.cases.usb3_gen1_typeA }}x USB 3.1 Gen 1 Type-A
							</p>
							<p v-if="+parts.cases.audio_mic" class="specs-description">
								- {{ parts.cases.audio_mic }}x Headset Audio Jack
							</p>
						</template>
					</div>
				</div>
				<div class="specs-product">
					<p class="specs-category">CPU</p>
					<div class="specs-info">
						<h5 class="specs-product-name">{{ parts.cpus.name }}</h5>
					</div>
				</div>
				<div class="specs-product">
					<p class="specs-category">Graphics</p>
					<div class="specs-info">
						<h5 class="specs-product-name">
							{{ parts.gpus.title }}
							<span v-show="upgrade && totalBuilds > 1">Upgraded</span>
						</h5>
						<template v-if="displayDetails">
							<p v-if="parts.gpus.rgb_lighting" class="specs-description">
								- {{ parts.gpus.rgb_lighting }}
							</p>
							<p v-if="parts.gpus.prebuid_hdmi_port" class="specs-description">
								- {{ parts.gpus.prebuid_hdmi_port }}
							</p>
							<p v-if="parts.gpus.dvi_port" class="specs-description">
								- {{ parts.gpus.dvi_port }}
							</p>
							<p v-if="parts.gpus.displayport" class="specs-description">
								- {{ parts.gpus.displayport }}
							</p>
						</template>
					</div>
				</div>
				<div class="specs-product">
					<p class="specs-category">Motherboard</p>
					<div class="specs-info">
						<h5 class="specs-product-name">{{ parts.motherboards.name }}</h5>
						<template v-if="displayDetails">
							<p
								v-if="+parts.motherboards.usb3_gen2_ports"
								class="specs-description"
							>
								- {{ parts.motherboards.usb3_gen2_ports }}x USB 3.1 Gen 2 ports
							</p>
							<p
								v-if="+parts.motherboards.usb3_gen1_ports"
								class="specs-description"
							>
								- {{ parts.motherboards.usb3_gen1_ports }}x USB 3.1 Gen 1 ports
							</p>
							<p v-if="+parts.motherboards.hdmi_14b" class="specs-description">
								- {{ parts.motherboards.hdmi_14b }}x HDMI™ 1.4b
							</p>
							<p
								v-if="+parts.motherboards.lan_rj45_port"
								class="specs-description"
							>
								- {{ parts.motherboards.lan_rj45_port }}x LAN (RJ45) port
							</p>
							<p
								v-if="+parts.motherboards.audio_jack_51channel"
								class="specs-description"
							>
								- {{ parts.motherboards.audio_jack_51channel }}x Audio jack
								(5.1-Channel)
							</p>
							<p
								v-if="parts.motherboards.ram_slots_prebuilds"
								class="specs-description"
							>
								- {{ parts.motherboards.ram_slots_prebuilds }}
							</p>
							<p v-if="parts.motherboards.usb_ports" class="specs-description">
								- {{ parts.motherboards.usb_ports }}
							</p>
							<p
								v-if="parts.motherboards.audio_jacks"
								class="specs-description"
							>
								- {{ parts.motherboards.audio_jacks }}
							</p>
							<p v-if="parts.motherboards.ps2_port" class="specs-description">
								- {{ parts.motherboards.ps2_port }}
							</p>
							<p v-if="parts.motherboards.usb_type_c" class="specs-description">
								- {{ parts.motherboards.usb_type_c }}
							</p>
							<p
								v-if="parts.motherboards.ethernet_port"
								class="specs-description"
							>
								- {{ parts.motherboards.ethernet_port }}
							</p>
							<p v-if="parts.motherboards.hdmi_ports" class="specs-description">
								- {{ parts.motherboards.hdmi_ports }}
							</p>
							<p
								v-if="parts.motherboards.prebuild_display_port"
								class="specs-description"
							>
								- {{ parts.motherboards.prebuild_display_port }}
							</p>
						</template>
					</div>
				</div>
				<template v-if="!displayDetails">
					<div class="specs-product">
						<p class="specs-category">RAM</p>
						<div class="specs-info">
							<h5 class="specs-product-name">
								{{ parts.ram.name }}
								<small>(max&nbsp;speed)</small>
								<b
									v-html="
										computeRamCapacity(
											parts.ram.capacity,
											parts.ram.slots,
											parts.ram.quantity
										)
									"
								/>
								<a
									href="https://support.nzxt.com/hc/en-us/articles/360038630493-RAM-XMP-Guide-FAQ"
									target="_blank"
								>
									More Info
								</a>
							</h5>
							<template v-if="displayDetails">
								<p v-if="parts.ram.rgb_lighting" class="specs-description">
									- {{ parts.ram.rgb_lighting }}
								</p>
							</template>
						</div>
					</div>
					<div class="specs-product">
						<p class="specs-category">CPU Cooler</p>
						<div class="specs-info">
							<h5 class="specs-product-name">{{ parts.coolers.name }}</h5>
						</div>
					</div>
				</template>
			</div>
			<div class="col-md-5">
				<template v-if="displayDetails">
					<div class="specs-product">
						<p class="specs-category">RAM</p>
						<div class="specs-info">
							<h5 class="specs-product-name">
								{{ parts.ram.name }}
								<small>(max speed)</small>
								<b
									v-html="
										computeRamCapacity(
											parts.ram.capacity,
											parts.ram.slots,
											parts.ram.quantity
										)
									"
								/>
								<a
									href="https://support.nzxt.com/hc/en-us/articles/360038630493-RAM-XMP-Guide-FAQ"
									target="_blank"
								>
									More Info
								</a>
							</h5>
							<template v-if="displayDetails">
								<p v-if="parts.ram.rgb_lighting" class="specs-description">
									- {{ parts.ram.rgb_lighting }}
								</p>
							</template>
						</div>
					</div>
					<div class="specs-product">
						<p class="specs-category">CPU Cooler</p>
						<div class="specs-info">
							<h5 class="specs-product-name">{{ parts.coolers.name }}</h5>
						</div>
					</div>
				</template>
				<div class="specs-product">
					<p class="specs-category">Storage</p>
					<div class="specs-info">
						<p class="specs-product-name">
							<template v-for="(ssd, pId, index) in parts.storage.SSD">
								{{ ssd.title }}
								{{ ssd.capacity }}
								<span
									:key="`upgrade-${ssd.id}`"
									v-show="upgrade && !index && totalBuilds > 1"
								>
									Upgraded
								</span>
							</template>
						</p>
						<p v-if="parts.storage.HDD" class="specs-product-name">
							<template v-for="(hdd, pId, index) in parts.storage.HDD">
								{{ hdd.title }}
								{{ hdd.capacity }}
								<span
									:key="`upgrade-${hdd.id}`"
									v-show="upgrade && !index && totalBuilds > 1"
								>
									Upgraded
								</span>
							</template>
						</p>
					</div>
				</div>
				<div class="specs-product">
					<p class="specs-category">Power Supply</p>
					<div class="specs-info">
						<h5 class="specs-product-name">{{ parts.psus.name }}</h5>
					</div>
				</div>
				<div v-if="parts.extras" class="specs-product">
					<p class="specs-category">Accessories</p>
					<div class="specs-info">
						<h5 class="specs-product-name">
							<template v-for="extra in parts.extras">
								{{ extra.name }}<br :key="`extras-${extra.id}`">
							</template>
						</h5>
					</div>
				</div>
				<div class="specs-product">
					<p class="specs-category">Software</p>
					<div class="specs-info">
						<h5 class="specs-product-name">{{ parts.softwares.name }}</h5>
					</div>
				</div>
				<div class="specs-product">
					<p class="specs-category">Warranty</p>
					<div class="specs-info">
						<p class="specs-description warranty">
							2-year warranty on all parts &amp; labor
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { computeRamCapacity } from "@u/store-helpers";
import PromoSalesTimed from "@/components/promo-sales-timed";
import HolidayTag from "@c/generals/holiday-tag";

export default {
	name: "PcSpecs",
	components: {
		PromoSalesTimed,
		HolidayTag
	},
	props: {
		displayDetails: {
			type: Boolean,
			default: true
		},
		parts: {
			type: Object,
			required: true
		},
		upgrade: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		...mapState({
			cart: state => state.StarterPc.cart
		}),
		...mapGetters({
			totalBuilds: "StarterPc/totalBuilds"
		})
	},
	methods: {
		computeRamCapacity
	}
};
</script>

<style lang="scss" scoped>
@import "~@s/_asset-mixers.scss";

.pc-specs::v-deep {
	.holiday-tag {
		display: inline-block;
	}
}

.pc-specs {
	overflow: hidden;

	.row-padding {
		padding-top: 20px;
	}

	&-first-col {
		@media (min-width: 1200px) {
			margin-right: -35px;
		}
	}

	.col-md-offset-1 {
		@media (min-width: 992px) {
			margin-left: 13.333333%;
		}
	}

	.specs-category {
		flex: 0 1 100px;
		margin-right: 20px;
		color: #4b4a5f;
		margin-top: -2px;

		@media (min-width: 320px) and (max-width: 1024px) {
			flex: 0 1 35%;
			margin-right: 0;
		}
	}

	.specs-category,
	.specs-product-name {
		font-weight: bold;

		span {
			color: $orchid;
			text-transform: uppercase;
		}

		small {
			font-size: inherit;
		}
	}

	.specs-info {
		flex: 1;
		@media (min-width: 320px) and (max-width: 1024px) {
			width: 65%;
		}
	}

	.specs-description {
		margin-bottom: 4px;
		color: #646f7d;
	}

	.specs-product {
		display: flex;
		margin-bottom: 20px;

		&-name {
			margin: 0 0 8px;
			color: #4c4a60;

			@media (min-width: 1366px) {
				width: 300px;
			}
		}
	}

	.warranty {
		font-weight: bold;
		color: #4c4a60;
	}
}
</style>

<style lang="scss">
.specs-product-name {
	small {
		color: inherit !important;
		font-size: inherit !important;
		font-weight: inherit !important;
	}
}
</style>
